/**
 * Client HTTP minimaliste pour l'API ReceiptAI.
 * Base URL : `/api` en dev (proxy webpack → backend) ; surchargeable via
 * `RECEIPT_AI_API_URL` (fichier .env) — doit alors être une URL complète
 * avec schéma (http:// ou https://), sinon un chemin absolu commençant par `/`.
 */

/** Normalise la base URL : vide → '/api', et valide le schéma si absolue. */
function resolveBaseUrl(): string {
  const raw = process.env.RECEIPT_AI_API_URL?.trim();
  if (!raw) return '/api';
  // Chemin relatif à la racine (ex. '/api') : valide tel quel.
  if (raw.startsWith('/')) return raw.replace(/\/$/, '');
  // URL absolue : le schéma est requis.
  if (!/^https?:\/\//.test(raw)) {
    console.warn(
      `[API] RECEIPT_AI_API_URL="${raw}" est invalide (schéma http(s) manquant). ` +
        `Repli sur '/api'. Exemple attendu : http://localhost:4000`,
    );
    return '/api';
  }
  return raw.replace(/\/$/, '');
}

const BASE_URL = resolveBaseUrl();

/** Fournisseur de token, enregistré par la feature auth (évite un cycle d'import). */
let tokenGetter: (() => string | null) | null = null;

/** Enregistre la fonction qui fournit le token JWT courant. */
export function registerTokenGetter(getter: () => string | null): void {
  tokenGetter = getter;
}

/** Corps d'erreur normalisé renvoyé par le backend. */
interface ApiErrorBody {
  error?: { message?: string; statusCode?: number; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenGetter?.();
  const isFormData = init?.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'API injoignable. Le serveur backend est-il démarré ?');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.error?.message ?? `Erreur ${response.status}`,
      body?.error?.details,
    );
  }

  return body as T;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: <T>(path: string, body: FormData) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
